/**
 * useVideoAnalytics — GA4 instrumentation for the video player.
 *
 * Hooks into VideoCompositionPlayer callbacks (onStateChange, onSlideStart,
 * onVideoEnd, onError) and fires GA4 events.  Runs entirely client-side;
 * no custom backend needed — all data lands in the GA4 property.
 *
 * GA4 events fired:
 *   video_start          — first play
 *   video_progress       — 10 / 25 / 50 / 75 / 90 % milestones
 *   video_complete       — video finishes
 *   video_pause          — user pauses
 *   video_resume         — user resumes after pause
 *   video_seek           — currentTime jumps > 2 s
 *   video_slide_view     — new slide begins
 *   video_slide_engage   — leaving a slide (includes dwell time)
 *   video_mode_change    — video ↔ slides mode switch
 *   video_speed_change   — playback rate change
 *   video_fullscreen     — fullscreen toggle
 *   video_share_click    — share / copy-link
 *   video_tab_away       — tab-hidden pause
 *   video_error          — player error
 *   video_heartbeat      — periodic ping (every 30 s while playing)
 */

import { useEffect, useRef, useCallback } from 'react';
import type { VideoPlaybackState, VideoManifest } from '../types/videoManifest';
import { AnalyticsSpine } from '../analytics/spine';
import { classifySeek, isFrameworkPlayChange } from '../analytics/classifyChange';
import type { AnalyticsVariant } from '@agentic-ivy/shared-types';
import { isGa4Allowed } from '../analytics/consentGate';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VideoAnalyticsConfig {
  /** GA4 measurement ID */
  measurementId?: string;
  /** Share token (identifies the video) */
  shareToken: string;
  /** Loaded manifest */
  manifest: VideoManifest | null;
  /** Whether the player is embedded in an iframe */
  isEmbed?: boolean;
  /** Player variant for the in-house spine. Defaults to 'produced'. */
  variant?: AnalyticsVariant;
}

interface SlideSession {
  slideId: string;
  slideIndex: number;
  startedAt: number; // Date.now()
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// GA4 is loaded by the app-wide consent gate (src/analytics/consentGate.ts), which
// owns Consent Mode, the 4k_consent cookie and the gtag.js load. The hook only
// sends events, and only while GA4 is allowed (consent granted | notice, and not a
// provider-launched `actor` session).
function sendEvent(name: string, params: Record<string, any>) {
  if (!isGa4Allowed()) return;
  window.gtag?.('event', name, params);
}

function getViewerId(): string {
  const key = 'ivy_viewer_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVideoAnalytics(config: VideoAnalyticsConfig) {
  const { shareToken, manifest, isEmbed = false, variant = 'produced' } = config;

  // In-house spine (cookieless T0 — runs always, independent of GA4 consent).
  // Instantiated in the lifecycle effect so each mount gets a fresh View.
  const spineRef = useRef<AnalyticsSpine | null>(null);
  const spineHeartbeat = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs to track state across renders without causing re-renders
  const viewerId = useRef(getViewerId());
  const hasStarted = useRef(false);
  const progressFired = useRef<Set<number>>(new Set());
  const prevState = useRef<VideoPlaybackState | null>(null);
  const slideSession = useRef<SlideSession | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSeekAt = useRef(0);

  // Emit one spine seek, deduped — a slide-nav jump trips both the onSlideStart
  // (slide_nav) and onStateChange (scrub) detectors within ~the same tick.
  const emitSpineSeek = useCallback(
    (fromMs: number, toMs: number, slideId: string, via: string | null, ph: number | null, user = true) => {
      const now = Date.now();
      if (now - lastSeekAt.current < 800) return;
      lastSeekAt.current = now;
      spineRef.current?.track('video_seek', { from_ms: fromMs, to_ms: toMs, slide_id: slideId, user, ...(via ? { via } : {}) }, ph);
    },
    [],
  );

  // Common params attached to every event
  const baseParams = useCallback(() => ({
    video_share_token: shareToken,
    video_title: manifest?.metadata?.title || '(untitled)',
    video_duration: manifest?.metadata?.totalDuration || 0,
    video_slide_count: manifest?.metadata?.slideCount || 0,
    viewer_id: viewerId.current,
    is_embed: isEmbed,
  }), [shareToken, manifest, isEmbed]);

  // --- Spine lifecycle: impression on mount, view_end on unmount ---
  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    const spine = new AnalyticsSpine({
      shareToken,
      projectId: shareToken,
      variant,
      collectUrl: `${apiBase}/api/analytics/collect`,
    });
    spineRef.current = spine;
    spine.start();
    return () => {
      spine.stop();
      spineRef.current = null;
    };
  }, [shareToken, variant]);

  // --- Set GA4 user properties once per session ---
  useEffect(() => {
    if (!manifest || !isGa4Allowed()) return;

    window.gtag?.('set', 'user_properties', {
      viewer_id: viewerId.current,
      device_type: getDeviceType(),
      is_embed: String(isEmbed),
    });
  }, [manifest, isEmbed]);

  // --- Heartbeat (every 30s while playing) ---
  const startHeartbeat = useCallback(() => {
    if (!heartbeatTimer.current) {
      heartbeatTimer.current = setInterval(() => {
        const s = prevState.current;
        if (!s || !s.isPlaying) return;
        sendEvent('video_heartbeat', {
          ...baseParams(),
          current_time: Math.round(s.currentTime),
          slide_id: s.currentSlideId,
          slide_index: s.currentSlideIndex,
          playback_rate: s.playbackRate,
        });
      }, 30_000);
    }
    // Spine heartbeat is finer (~8s) per §16.1 — drives drop-off resolution.
    if (!spineHeartbeat.current) {
      spineHeartbeat.current = setInterval(() => {
        const s = prevState.current;
        if (!s || !s.isPlaying) return;
        spineRef.current?.track(
          'video_heartbeat',
          { slide_id: s.currentSlideId, slide_index: s.currentSlideIndex, playback_rate: s.playbackRate },
          Math.round(s.currentTime * 1000),
        );
      }, 8_000);
    }
  }, [baseParams]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    if (spineHeartbeat.current) {
      clearInterval(spineHeartbeat.current);
      spineHeartbeat.current = null;
    }
  }, []);

  useEffect(() => () => stopHeartbeat(), [stopHeartbeat]);

  // --- Flush slide dwell on unmount ---
  useEffect(() => {
    return () => {
      flushSlideDwell();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cumulative playhead start (sec) of a slide, from the manifest durations.
  /**
   * WATCH METRICS RIDE THE NARRATION CLOCK, NOT THE RAW TIMELINE.
   *
   * `totalDuration` / `slide.duration` are the engine clock: they include the
   * 120s VCC hold ceiling every interactive slide carries, plus the in-file gate
   * windows a learner never hears (on solve the GateRunner seeks straight from
   * pauseAt to resumeAt). For the 7.EE.A.1 pilot that is 849s against 251s of
   * narration — 598 seconds nobody can watch.
   *
   * Scoring coverage against it under-reports by roughly 3x: a learner who
   * watched 3m30s of the 4m11s lesson, answered every question and completed it
   * was reported at 25%. The player chrome already renders the narration clock;
   * the spine was the last consumer still on raw time.
   *
   * The last observed display clock is kept here because flushSlideDwell runs
   * outside onStateChange and still needs the same axis the coverage vector is
   * indexed on. Manifests baked before narration metadata existed report no
   * display clock and degrade to the raw timeline.
   */
  const displayClock = useRef<{
    time: number;
    duration: number;
    slideStarts: number[];
    slideDurations: number[];
  }>({ time: 0, duration: 0, slideStarts: [], slideDurations: [] });

  const readDisplayClock = useCallback((state: VideoPlaybackState) => {
    const duration = Number(state.displayDuration) || 0;
    if (duration > 0) {
      displayClock.current = {
        time: Number(state.displayTime) || 0,
        duration,
        slideStarts: state.displaySlideStarts ?? [],
        slideDurations: state.displaySlideDurations ?? [],
      };
    } else {
      displayClock.current = {
        time: Number(state.currentTime) || 0,
        duration: Number(state.totalDuration) || 0,
        slideStarts: [],
        slideDurations: [],
      };
    }
    return displayClock.current;
  }, []);

  const slideStartSec = useCallback(
    (index: number) => {
      // Prefer the narration clock's own slide offsets; they are the axis the
      // coverage vector is indexed on.
      const starts = displayClock.current.slideStarts;
      if (starts.length > index) return starts[index];
      const slides = manifest?.slides ?? [];
      let acc = 0;
      for (let i = 0; i < index && i < slides.length; i++) acc += slides[i].duration || 0;
      return acc;
    },
    [manifest],
  );

  // --- Flush slide engagement when leaving a slide ---
  function flushSlideDwell() {
    const ss = slideSession.current;
    if (!ss) return;
    const dwellMs = Date.now() - ss.startedAt;
    if (dwellMs < 500) return; // ignore sub-500ms flickers
    const slide = manifest?.slides?.[ss.slideIndex];
    const durSec = slide?.duration || 0;
    sendEvent('video_slide_engage', {
      ...baseParams(),
      slide_id: ss.slideId,
      slide_index: ss.slideIndex,
      slide_title: slide?.title || '',
      slide_duration: durSec,
      dwell_time_sec: Math.round(dwellMs / 1000),
      attention_score: durSec ? Math.round(((dwellMs / 1000) / durSec) * 100) / 100 : 0,
    });
    // Spine attention is coverage-based (% of the slide's seconds actually
    // watched), so pausing/lingering can't push it past 1.0. It measures against
    // the slide's NARRATION length — the raw slide.duration is the 120s hold
    // ceiling, which would score every interactive slide at a fraction of the
    // truth.
    const coverSec = displayClock.current.slideDurations[ss.slideIndex] || durSec;
    const attention = coverSec
      ? spineRef.current?.coverageRatio(slideStartSec(ss.slideIndex), coverSec) ?? 0
      : 0;
    spineRef.current?.track('video_slide_engage', {
      slide_id: ss.slideId,
      slide_index: ss.slideIndex,
      slide_title: slide?.title || '',
      slide_duration: durSec,
      dwell_time_sec: Math.round(dwellMs / 1000),
      attention_score: Math.round(attention * 100) / 100,
    });
    slideSession.current = null;
  }

  // =======================================================================
  // Public callbacks — wire these into the player component
  // =======================================================================

  /** Call on every VideoPlaybackState update from onStateChange */
  const onStateChange = useCallback((state: VideoPlaybackState) => {
    const prev = prevState.current;
    prevState.current = state;

    if (!manifest) return;

    const spine = spineRef.current;
    const ph = Math.round(state.currentTime * 1000);

    // ---- per-second coverage vector (only while playing; seeks don't backfill)
    // Ticked in DISPLAY seconds so the vector is indexed on the same clock its
    // denominator is measured in. Event playhead_ms stays RAW: gate positions
    // correlate against the manifest's raw pauseAt, and seeking is raw-space.
    const clock = readDisplayClock(state);
    if (state.isPlaying) spine?.tickSecond(clock.time);

    // ---- feed watch-metric accumulators (furthest reached, playing time, %-viewed)
    spine?.progress(ph, Math.round(clock.duration * 1000), state.isPlaying);

    // ---- video_start (first play) ----
    if (state.isPlaying && !hasStarted.current) {
      hasStarted.current = true;
      sendEvent('video_start', {
        ...baseParams(),
        referrer: document.referrer || '(direct)',
        device_type: getDeviceType(),
      });
      spine?.track('video_start', { slide_id: state.currentSlideId, slide_index: state.currentSlideIndex }, ph);
      startHeartbeat();
    }

    // ---- video_pause / video_resume ----
    // A slide transition pauses/resumes the clock internally; tag those as
    // framework so they aren't read as the viewer pausing.
    if (prev) {
      const framework = isFrameworkPlayChange(state.changeSource);
      const playSource = framework ? 'transition' : 'user';
      if (prev.isPlaying && !state.isPlaying && !state.pausedByVisibility) {
        sendEvent('video_pause', {
          ...baseParams(),
          current_time: Math.round(state.currentTime),
          slide_id: state.currentSlideId,
          slide_index: state.currentSlideIndex,
          percent_watched: Math.round((state.currentTime / state.totalDuration) * 100),
          source: playSource,
        });
        spine?.track('video_pause', { slide_id: state.currentSlideId, slide_index: state.currentSlideIndex, source: playSource }, ph);
        if (!framework) stopHeartbeat();
      }
      if (!prev.isPlaying && state.isPlaying && hasStarted.current) {
        // Only count resume if not the initial start
        if (prev.currentTime > 0) {
          sendEvent('video_resume', {
            ...baseParams(),
            current_time: Math.round(state.currentTime),
            source: playSource,
          });
          spine?.track('video_resume', { slide_id: state.currentSlideId, slide_index: state.currentSlideIndex, source: playSource }, ph);
        }
        startHeartbeat();
      }
    }

    // ---- video_seek ----
    // Classify from the controller's stamped changeSource: a human scrub/seek/
    // nav vs the framework moving the playhead on a slide transition. Framework
    // jumps are retained but labeled (via='auto_advance'/'transition', user=false)
    // so they never count as user seeks. Legacy builds fall back to the delta.
    if (prev && hasStarted.current) {
      const sameSlide = state.currentSlideId === prev.currentSlideId;
      const seek = classifySeek(state.changeSource, prev.currentTime, state.currentTime, sameSlide);
      if (seek) {
        sendEvent('video_seek', {
          ...baseParams(),
          seek_from: Math.round(prev.currentTime),
          seek_to: Math.round(state.currentTime),
          slide_id: state.currentSlideId,
          via: seek.via,
          user: seek.user,
        });
        // Cross-slide user navs are emitted from onSlideStart (captured even
        // while paused); here we emit within-slide scrubs and framework jumps.
        if (sameSlide || !seek.user) {
          emitSpineSeek(Math.round(prev.currentTime * 1000), ph, state.currentSlideId, seek.via, ph, seek.user);
        }
      }
    }

    // ---- video_progress milestones ----
    // Coverage-based: a milestone fires only once that fraction of the video has
    // actually been WATCHED, not when the playhead happens to pass it. In a
    // master-timeline video the framework jumps the playhead to each slide's
    // position; the old playhead-% test let those jumps fire 50/75/90% without
    // the viewer seeing the content. Coverage (unique watched seconds) is immune.
    if (clock.duration > 0 && progressFired.current.size < 5) {
      const coveragePct = (spine?.coverageRatio(0, clock.duration) ?? 0) * 100;
      for (const milestone of [10, 25, 50, 75, 90]) {
        if (coveragePct >= milestone && !progressFired.current.has(milestone)) {
          progressFired.current.add(milestone);
          sendEvent('video_progress', {
            ...baseParams(),
            video_percent: milestone,
            current_time: Math.round(state.currentTime),
            slide_id: state.currentSlideId,
            slide_index: state.currentSlideIndex,
          });
          spine?.track(
            'video_progress',
            { percent: milestone, slide_id: state.currentSlideId, slide_index: state.currentSlideIndex },
            ph,
          );
        }
      }
    }

    // ---- video_tab_away ----
    if (prev && !prev.pausedByVisibility && state.pausedByVisibility) {
      sendEvent('video_tab_away', {
        ...baseParams(),
        current_time: Math.round(state.currentTime),
        slide_id: state.currentSlideId,
        percent_watched: Math.round((state.currentTime / state.totalDuration) * 100),
      });
    }

    // ---- speed change ----
    if (prev && prev.playbackRate !== state.playbackRate) {
      sendEvent('video_speed_change', {
        ...baseParams(),
        from_rate: prev.playbackRate,
        to_rate: state.playbackRate,
      });
      spine?.track('video_ratechange', { playback_rate: state.playbackRate }, ph);
    }
  }, [manifest, baseParams, startHeartbeat, stopHeartbeat, emitSpineSeek]);

  /** Call from onSlideStart callback */
  const onSlideStart = useCallback((slideId: string) => {
    if (!manifest) return;

    const prevIdx = slideSession.current?.slideIndex ?? null;

    // Flush previous slide engagement
    flushSlideDwell();

    const idx = manifest.slides.findIndex((s) => s.id === slideId);
    const slide = manifest.slides[idx];

    // A non-adjacent slide jump = a seek (captured even while paused, which the
    // onStateChange detector misses).
    if (prevIdx != null && idx >= 0 && Math.abs(idx - prevIdx) > 1) {
      const toMs = Math.round(slideStartSec(idx) * 1000);
      emitSpineSeek(Math.round((prevState.current?.currentTime ?? 0) * 1000), toMs, slideId, 'slide_nav', toMs);
    }

    slideSession.current = {
      slideId,
      slideIndex: idx,
      startedAt: Date.now(),
    };

    sendEvent('video_slide_view', {
      ...baseParams(),
      slide_id: slideId,
      slide_index: idx,
      slide_title: slide?.title || '',
      slide_duration: slide?.duration || 0,
    });
    spineRef.current?.track(
      'video_slide_view',
      { slide_id: slideId, slide_index: idx, slide_title: slide?.title || '', slide_duration: slide?.duration || 0 },
      Math.round((prevState.current?.currentTime ?? 0) * 1000),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, baseParams, slideStartSec, emitSpineSeek]);

  /** Call from onVideoEnd callback */
  const onVideoEnd = useCallback(() => {
    flushSlideDwell();
    stopHeartbeat();

    sendEvent('video_complete', {
      ...baseParams(),
      total_slides_viewed: progressFired.current.size > 0
        ? manifest?.metadata?.slideCount || 0
        : 0,
    });
    spineRef.current?.track('video_complete', { percent: 100 }, Math.round((prevState.current?.currentTime ?? 0) * 1000));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, baseParams, stopHeartbeat]);

  /** Call from onError callback */
  const onError = useCallback((message: string) => {
    sendEvent('video_error', {
      ...baseParams(),
      error_message: message.substring(0, 100),
    });
    spineRef.current?.track('video_error', { message: message.substring(0, 200) });
  }, [baseParams]);

  // =======================================================================
  // Interaction trackers — call these from UI event handlers
  // =======================================================================

  /** Track video ↔ slides mode change */
  const trackModeChange = useCallback((mode: 'video' | 'slides') => {
    sendEvent('video_mode_change', {
      ...baseParams(),
      player_mode: mode,
      current_time: Math.round(prevState.current?.currentTime || 0),
      slide_index: prevState.current?.currentSlideIndex ?? 0,
    });
  }, [baseParams]);

  /** Track fullscreen toggle */
  const trackFullscreen = useCallback((isFullscreen: boolean) => {
    sendEvent('video_fullscreen', {
      ...baseParams(),
      fullscreen: isFullscreen,
    });
  }, [baseParams]);

  /** Track share / copy-link click */
  const trackShareClick = useCallback(() => {
    sendEvent('video_share_click', {
      ...baseParams(),
      current_time: Math.round(prevState.current?.currentTime || 0),
    });
  }, [baseParams]);

  /** Track seek via controls (complements auto-detected seeks) */
  const trackSeek = useCallback((fromTime: number, toTime: number) => {
    sendEvent('video_seek', {
      ...baseParams(),
      seek_from: Math.round(fromTime),
      seek_to: Math.round(toTime),
      slide_id: prevState.current?.currentSlideId || '',
    });
  }, [baseParams]);

  return {
    // Wire these into VideoCompositionPlayer callbacks
    onStateChange,
    onSlideStart,
    onVideoEnd,
    onError,
    // Wire these into UI interaction handlers
    trackModeChange,
    trackFullscreen,
    trackShareClick,
    trackSeek,
  };
}
